import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { Organization } from '../models/Organization';
import { OrganizationService } from '../services/OrganizationService';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  refreshOrganization: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, loading: authLoading } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether an org has resolved at least once, so background refetches
  // don't flip `loading` back on and flash the nav empty (OMN-318).
  const hasLoadedOrgRef = useRef(false);

  const fetchOrganization = useCallback(async () => {
    // Don't resolve loading while auth is still settling — currentUser (and its
    // orgId) may not be populated yet. Resolving to false here with a null
    // organization makes consumers (e.g. permission checks) read "org loaded,
    // type unknown" and wrongly deny access. Stay in the loading state until
    // auth is done so the org type is reliably present before we report ready.
    if (authLoading) {
      return;
    }

    if (!currentUser?.orgId) {
      setOrganization(null);
      setLoading(false);
      hasLoadedOrgRef.current = false;
      return;
    }

    // Show a loading state only for the FIRST resolve of an org so consumers (the
    // sidebar nav, ProtectedRoute permission gates) wait for the org type before
    // evaluating access. Background refetches — the realtime org-settings channel,
    // or a re-resolve after the duplicate auth event on login — keep the already-
    // loaded org on screen instead of flashing the nav empty each time (OMN-318).
    if (!hasLoadedOrgRef.current) setLoading(true);
    try {
      const org = await OrganizationService.getOrganization(currentUser.orgId);
      setOrganization(org);
      hasLoadedOrgRef.current = true;
    } catch (err: any) {
      logger.error('[OrganizationProvider] Error fetching organization:', err);
      setError(err?.message || 'Failed to fetch organization data');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.orgId, authLoading]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // Realtime: propagate org settings changes (name, ICAO, settings) to all open
  // sessions without a full reload. Silent refetch — no loading flash.
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  useEffect(() => {
    const orgId = currentUser?.orgId;
    if (!orgId) return;
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    const channel = supabase
      .channel(`organization:${orgId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${orgId}` },
        () => { fetchOrganization(); })
      .subscribe();
    channelRef.current = channel;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [currentUser?.orgId, fetchOrganization]);

  const refreshOrganization = async () => {
    setLoading(true);
    await fetchOrganization();
  };

  const value = {
    organization,
    loading,
    error,
    refreshOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
} 